// DashboardPage — landing screen with KPIs and recent activity.
//
// Composition: 4 KPI cards on top, then a 3-column grid:
//   • Recent activity (10 audit log entries, scoped to articles)
//   • Pinned drafts (5 most recently updated drafts with cover thumb)
//   • Upcoming schedule (5 nearest scheduled articles)
//
// Each section degrades gracefully — if the underlying query fails or
// returns 0 items, it shows an empty state instead of breaking the layout.

import { useEffect, useState } from "react";
import { list, fileUrl } from "../lib/pb";
import { navigate } from "../App";
import { useT } from "../lib/i18n";
import type { Article, AuditEntry } from "../lib/types";
import { StatusBadge, formatDate } from "./ArticlesPage";

interface Kpi {
  published: number;
  drafts: number;
  totalViews: number;
  total: number;
}

export default function DashboardPage() {
  const t = useT();
  const [kpi, setKpi] = useState<Kpi>({ published: 0, drafts: 0, totalViews: 0, total: 0 });
  const [recent, setRecent] = useState<AuditEntry[]>([]);
  const [drafts, setDrafts] = useState<Article[]>([]);
  const [scheduled, setScheduled] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      // Fan-out the 4 queries in parallel — they are cheap and independent.
      const [pub, dr, sc, audit, all] = await Promise.all([
        list<Article>("articles", { perPage: 1, filter: "status='published'" }),
        list<Article>("articles", { perPage: 5, sort: "-updated", filter: "status='draft'", expand: "author" }),
        list<Article>("articles", { perPage: 5, sort: "publish_at", filter: "status='scheduled'" }),
        list<AuditEntry>("audit_log", { perPage: 10, sort: "-created", filter: "target_collection='articles'", expand: "actor" }),
        list<Article>("articles", { perPage: 500, sort: "-updated" }),
      ]);
      setRecent(audit.items);
      setDrafts(dr.items);
      setScheduled(sc.items);

      const totalViews = all.items.reduce((s, a) => s + (a.view_count || 0), 0);
      setKpi({
        published: pub.totalItems,
        drafts: dr.totalItems,
        totalViews,
        total: all.totalItems,
      });
    } catch (e) {
      // Soft-fail — leave the page mostly empty rather than blocking.
      console.error("Dashboard load failed:", e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t.dashTitle}</h1>
        <p className="page-subtitle">{t.dashSubtitle}</p>
      </div>

      {/* KPIs */}
      <div className="kpi-grid">
        <KpiCard label={t.dashKpiPublished} value={kpi.published} />
        <KpiCard label={t.dashKpiDrafts} value={kpi.drafts} />
        <KpiCard label={t.dashKpiViews} value={kpi.totalViews.toLocaleString()} />
        <KpiCard label={t.dashKpiArticles} value={kpi.total} />
      </div>

      {/* Analytics quick-link */}
      <a
        href="#/analytics"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          color: "var(--accent)",
          marginBottom: 16,
          padding: "6px 12px",
          border: "1px solid var(--border-1)",
          borderRadius: "var(--radius-md)",
          background: "var(--accent-soft)",
          textDecoration: "none",
          transition: "var(--transition)",
        }}
        onMouseEnter={(ev) => { (ev.currentTarget as HTMLAnchorElement).style.borderColor = "var(--accent)"; }}
        onMouseLeave={(ev) => { (ev.currentTarget as HTMLAnchorElement).style.borderColor = "var(--border-1)"; }}
      >
        <span>⊞</span>
        <span>{t.dashViewAnalytics}</span>
        <span style={{ color: "var(--text-3)" }}>→</span>
      </a>

      {loading ? (
        <div className="spinner-line">{t.loading}</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {/* Recent activity */}
          <section className="card" style={{ padding: 18 }}>
            <h2 style={sectionTitle}>{t.dashRecentActivity}</h2>
            {recent.length === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>{t.dashNoActivity}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {recent.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => {
                      if (e.target_collection === "articles" && !e.action.endsWith(".delete")) {
                        navigate(`/article/${e.target_id}`);
                      }
                    }}
                    style={{
                      display: "flex", flexDirection: "column", gap: 2,
                      padding: "8px 10px",
                      background: "transparent",
                      border: "none",
                      borderRadius: "var(--radius-md)",
                      cursor: "pointer", textAlign: "left",
                    }}
                    onMouseEnter={(ev) => ((ev.currentTarget as HTMLButtonElement).style.background = "var(--bg-2)")}
                    onMouseLeave={(ev) => ((ev.currentTarget as HTMLButtonElement).style.background = "transparent")}
                  >
                    <div style={{ fontSize: 12, color: "var(--text-1)" }}>
                      <span style={{ color: "var(--accent)" }}>{e.action}</span>
                      {" · "}
                      <span>{e.target_label || e.target_id}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                      {e.expand?.actor?.name || e.actor} · {formatDate(e.created)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Pinned drafts */}
          <section className="card" style={{ padding: 18 }}>
            <h2 style={sectionTitle}>{t.dashDraftsInProgress}</h2>
            {drafts.length === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>{t.dashNoDrafts}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {drafts.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      display: "flex", gap: 10, alignItems: "center",
                      padding: 6, borderRadius: "var(--radius-md)",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/article/${a.id}`)}
                    onMouseEnter={(ev) => ((ev.currentTarget as HTMLDivElement).style.background = "var(--bg-2)")}
                    onMouseLeave={(ev) => ((ev.currentTarget as HTMLDivElement).style.background = "transparent")}
                  >
                    {a.cover ? (
                      <img
                        src={fileUrl("articles", a.id, a.cover, "96x96")}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{ width: 48, height: 48, background: "var(--bg-3)", borderRadius: 4, flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-0)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.title}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                        {formatDate(a.updated)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Upcoming */}
          <section className="card" style={{ padding: 18 }}>
            <h2 style={sectionTitle}>{t.dashUpcomingSchedule}</h2>
            {scheduled.length === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>{t.dashNoScheduled}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {scheduled.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      display: "flex", flexDirection: "column", gap: 4,
                      padding: "8px 10px",
                      borderRadius: "var(--radius-md)",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/article/${a.id}`)}
                    onMouseEnter={(ev) => ((ev.currentTarget as HTMLDivElement).style.background = "var(--bg-2)")}
                    onMouseLeave={(ev) => ((ev.currentTarget as HTMLDivElement).style.background = "transparent")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <StatusBadge status={a.status} />
                      <span style={{ fontSize: 12, color: "var(--text-0)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.title}
                      </span>
                    </div>
                    {a.publish_at && (
                      <div style={{ fontSize: 11, color: "var(--accent)" }}>
                        {formatDate(a.publish_at)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{value}</span>
    </div>
  );
}

const sectionTitle: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 16,
  fontWeight: 600,
  margin: "0 0 14px",
  color: "var(--text-0)",
  letterSpacing: "-0.01em",
};
